import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { Redirect, useHistory, useParams } from 'react-router';
import { FullWidthPaddedColumn } from '../common/Containers';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { StyledParagraphLarge } from '../common/typography/Typography';
import { useTranslations } from '../localisation/translateText';
import { useGetData, useMutateData } from '../network/api/api';
import {
  getOrderInfoEndpoint,
  GetOrderInfoResponse,
  GetOrderInfoTransformedResponse,
  rescheduleBody,
  rescheduleEndpoint,
  RescheduleResponse,
} from '../network/api/endpoints';
import { GetAvailabilityTransformedData } from '../network/transformers/transformAvailabilityInfo';
import { transformOrderDetails } from '../network/transformers/transformOrderDetails';
import { OrderInfoErrorPage } from '../common/OrderInfoErrorPage';
import { ScheduleOrderForm } from '../scheduleOrder/ScheduleOrderForm';
import { translations, translationsType } from './translations';
import { breakPointLG } from '../globalConfig/styling/breakPoints';
import useMatchMedia from 'use-match-media-hook';
import { BrandLogo } from '../brandLogo/BrandLogo';
import { OrderItems } from '../common/orderItems/OrderItems';
import { OrderHelp } from '../orderHelp/OrderHelp';

const IntroTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DesktopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 70px 70px 0 150px;
  gap: 240px;
`;

const OrderHelpWrapper = styled.div`
  margin-top: 20px;
`;

const DesktopLeftSection = styled.div`
  flex: 0.7;
`;

const DesktopRightSection = styled.div`
  flex: 0.25;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BottomSection = styled.div`
  margin-top: 20px;
  border-top: 1px solid black;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const RescheduleContent = (): JSX.Element | null => {
  const urlParams = useParams<{ id: string }>();
  const { data: orderData, error } = useGetData<GetOrderInfoTransformedResponse, GetOrderInfoResponse>(
    `${getOrderInfoEndpoint}/${urlParams.id}`,
    (value) => transformOrderDetails(value),
  );
  const { translationsContent } = useTranslations<translationsType>(translations);
  const { mutate } = useMutateData();
  const history = useHistory();

  const [submitError, setSubmitError] = useState<boolean | string>(false);
  const [availabilityData, setAvailabilityData] = useState<GetAvailabilityTransformedData>();

  interface FormInput {
    delivery_date: string;
    delivery_slot_id: string;
    line1: string;
    line2?: string;
    city?: string;
    postcode: string;
    services: GetOrderInfoTransformedResponse['services'];
  }
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    async (_data) => {
      const data = _data as typeof _data & FormInput;
      const rescheduleOrderBody: rescheduleBody = {
        delivery_journey: {
          delivery_date: data.delivery_date,
          delivery_slot_id: data.delivery_slot_id,
        },
      };

      const rescheduleOrderResponse = await mutate<RescheduleResponse>(
        rescheduleEndpoint(urlParams.id),
        rescheduleOrderBody,
        'POST',
      );

      if ('error' in rescheduleOrderResponse) {
        setSubmitError(rescheduleOrderResponse.error);
      } else {
        history.push(`/orders/${urlParams.id}`);
      }
    },
    [history, mutate, urlParams.id],
  );

  const queries = [`(min-width: ${breakPointLG}px)`];
  const [desktop] = useMatchMedia(queries);

  if (error && Object.keys(error).length > 0) {
    return <OrderInfoErrorPage />;
  }

  if (!orderData) {
    return <LoadingSpinner />;
  }

  const lastJourneyLastTransition = orderData.journeys?.slice(-1)?.[0]?.journeyTransitions?.slice(-1)?.[0];
  if (lastJourneyLastTransition?.mostRecent && lastJourneyLastTransition.toState === 'completed') {
    return <Redirect to={`/orders/${urlParams.id}`} />;
  }

  if (desktop) {
    return (
      <DesktopWrapper>
        <DesktopLeftSection>
          <IntroTextContainer>
            <StyledParagraphLarge bold>{orderData.brandName}</StyledParagraphLarge>
            <StyledParagraphLarge>{`${translationsContent.hello} ${orderData.customer.name},`}</StyledParagraphLarge>
            <StyledParagraphLarge>{translationsContent.pleaseBookConvenientDateTimeForDelivery}</StyledParagraphLarge>
          </IntroTextContainer>
          <ScheduleOrderForm
            orderDetails={orderData}
            orderId={orderData.id}
            storeId={orderData.storeId}
            onSubmit={onSubmit}
            submitError={submitError}
            availabilityData={availabilityData}
            setAvailabilityData={setAvailabilityData}
          />
        </DesktopLeftSection>
        <DesktopRightSection>
          <BrandLogo brandName={orderData.brandName} brandLogoUrl={orderData.brandLogoUrl} />
          <OrderItems orderData={orderData} />
          <OrderHelpWrapper>
            <OrderHelp />
          </OrderHelpWrapper>
        </DesktopRightSection>
      </DesktopWrapper>
    );
  }
  return (
    <>
      <FullWidthPaddedColumn>
        <IntroTextContainer>
          <StyledParagraphLarge bold>{orderData.brandName}</StyledParagraphLarge>
          <StyledParagraphLarge>{`${translationsContent.hello} ${orderData.customer.name},`}</StyledParagraphLarge>
          <StyledParagraphLarge>{translationsContent.pleaseBookConvenientDateTimeForDelivery}</StyledParagraphLarge>
        </IntroTextContainer>
        <ScheduleOrderForm
          orderDetails={orderData}
          orderId={orderData.id}
          storeId={orderData.storeId}
          onSubmit={onSubmit}
          submitError={submitError}
          availabilityData={availabilityData}
          setAvailabilityData={setAvailabilityData}
        />
      </FullWidthPaddedColumn>
      <BottomSection>
        <OrderItems orderData={orderData} />
        <OrderHelp />
      </BottomSection>
    </>
  );
};
